<template>
  <v-container fluid>
    <v-row>
      <v-col cols="12">
        <v-card>
          <v-card-title>PMS Connector List</v-card-title>
          <v-form ref="searchForm" v-model="searchFormValid">
            <v-container>
              <v-row align="baseline">
                <v-col cols="3">
                  <v-select
                    v-model="searchType"
                    outlined
                    :items="searchTypes"
                    label="Search By"
                    :menu-props="{ maxHeight: 500 }"
                  >
                  </v-select>
                </v-col>
                <v-col cols="2">
                  <v-select
                    v-model="conditionType"
                    :items="conditionTypesForSearchType"
                    :rules="[(v) => !!v || 'Required']"
                    required
                    label="Search Condition"
                  >
                  </v-select>
                </v-col>
                <template v-if="selectedSearchType.type === 'date'">
                  <v-col :cols="conditionType === 'between' ? 2 : 3">
                    <v-datetime-picker
                      required
                      :rules="[(v) => !!v || 'Required']"
                      v-model="filterDateVal"
                      :label="selectedSearchType.label"
                      :hint="selectedSearchType.hint"
                      dateFormat="dd/MM/yyyy"
                      timeFormat="hh:mm aa"
                      :text-field-props="textFieldProps"
                      class="pa-10"
                    >
                      <template v-slot:dateIcon> Date </template>
                      <template v-slot:timeIcon> Time </template>
                    </v-datetime-picker>
                  </v-col>
                  <v-col cols="2">
                    <v-datetime-picker
                      v-if="conditionType === 'between'"
                      required
                      :rules="[(v) => !!v || 'Required']"
                      v-model="filterDateValTo"
                      :label="selectedSearchType.labelTo"
                      :hint="selectedSearchType.hintTo"
                      dateFormat="dd/MM/yyyy"
                      timeFormat="hh:mm aa"
                      :text-field-props="textFieldProps"
                      class="pa-10"
                    >
                      <template v-slot:dateIcon> Date </template>
                      <template v-slot:timeIcon> Time </template>
                    </v-datetime-picker>
                  </v-col>
                </template>
                <template
                  v-else-if="selectedSearchType.type.startsWith('text')"
                >
                  <v-col :cols="conditionType === 'between' ? 2 : 5">
                    <v-text-field
                      required
                      :rules="[(v) => !!v || 'Required']"
                      v-model="filterTextVal"
                      :label="selectedSearchType.label"
                      :hint="selectedSearchType.hint"
                    >
                    </v-text-field>
                  </v-col>
                  <v-col>
                    <v-text-field
                      required
                      :rules="[(v) => !!v || 'Required']"
                      v-if="conditionType === 'between'"
                      v-model="filterTextValTo"
                      :label="selectedSearchType.labelTo"
                      :hint="selectedSearchType.hintTo"
                    >
                    </v-text-field>
                  </v-col>
                </template>
                <template v-else-if="selectedSearchType.type === 'number'">
                  Number inputs
                </template>
                <template v-else-if="selectedSearchType.type === 'pmsType'">
                  <v-select
                    v-model="pmsType"
                    :items="pmsTypes"
                    item-text="name"
                    item-value="value"
                    label="PMS Type"
                  ></v-select>
                </template>
                <template v-else-if="selectedSearchType.type === 'statusType'">
                  <v-select
                    v-model="pmsConnectionStatusType"
                    :items="pmsConnectionStatusTypes"
                    item-text="name"
                    item-value="value"
                    label="PMS Connection Status"
                  ></v-select>
                </template>
                <v-col>
                  <v-btn
                    right
                    @click="getData()"
                    color="primary"
                    :loading="loading"
                    :disabled="!searchFormValid"
                    >Search</v-btn
                  >
                </v-col>
              </v-row>

              <v-row>
                <v-col cols="10">
                  <v-text-field
                    v-if="items.length > 0"
                    v-model="resultsFilter"
                    append-icon="mdi-magnify"
                    label="Filter results"
                    single-line
                    hide-details
                  ></v-text-field>
                </v-col>
                <v-col cols="2">
                  <template>
                    <v-tooltip bottom :disabled="!nextToken">
                      <template v-slot:activator="{ on }">
                        <span v-on="on">
                          <v-btn
                            :disabled="!nextToken"
                            :loading="loading"
                            color="success"
                            small
                            @click="getData(nextToken)"
                          >
                            Get More Results
                          </v-btn>
                        </span>
                      </template>
                      <span
                        >Your search matched more than 200 results. Get the next
                        batch of results for your search by clicking here.</span
                      >
                    </v-tooltip>
                  </template>
                </v-col>
              </v-row>
            </v-container>
          </v-form>
          <v-data-table
            :headers="headersForSearchType"
            :items="items"
            :loading="loading"
            :items-per-page="15"
            :options="listOptions"
            :search="resultsFilter"
            :show-select="remoteControlEngaged"
            item-key="sub"
            v-model="selected"
          >
            <template v-slot:item.usr="{ item }">
              <router-link
                :to="{ name: 'PmsConnector', params: { sub: item.sub } }"
                >{{ item.usr || item.sub }}</router-link
              >
            </template>
            <template v-slot:item.ctdTs="{ item }">
              <span v-if="item.ctdTs > 0">{{
                new Date(item.ctdTs).toLocaleString("en-AU")
              }}</span>
              <span v-else>n/a</span>
            </template>
            <template v-slot:item.heartbeatRcvd="{ item }">
              <span v-if="item.heartbeatRcvd">{{
                new Date(item.heartbeatRcvd).toLocaleString("en-AU")
              }}</span>
              <span v-else>n/a</span>
            </template>
            <template v-slot:item.availabilityLastProcTs="{ item }">
              <template
                v-if="
                  item.availabilityLastRcvdTs &&
                  !isNaN(item.availabilityLastRcvdTs)
                "
              >
                Rcvd:
                {{
                  new Date(Number(item.availabilityLastRcvdTs)).toLocaleString(
                    "en-AU"
                  )
                }}<br />
                Prcd:
              </template>
              <template v-if="item.availabilityLastProcTs">
                {{
                  new Date(item.availabilityLastProcTs).toLocaleString("en-AU")
                }}
              </template>
              <template v-else>n/a</template>
            </template>
            <template v-slot:item.pmsStatus="{ item }">
              <span v-if="item.pmsStatus">{{
                item.pmsStatus | pmsStatus
              }}</span>
              <span v-else>n/a</span>
            </template>
          </v-data-table>
          <v-card-actions>
            <v-switch
              v-model="remoteControlEngaged"
              label="Remote Control"
            ></v-switch>

            <v-spacer></v-spacer>

            <v-btn right @click="getData()" color="primary" :loading="loading"
              >Refresh</v-btn
            >
          </v-card-actions>
        </v-card>
        <v-container fluid v-if="remoteControlEngaged">
          <v-row align="baseline" justify="center">
            <v-col>
              <v-select
                v-model="remoteControlCommand"
                :items="remoteControlCommands"
                label="Command"
                outlined
              ></v-select>
            </v-col>
            <v-col>
              <v-btn
                :disabled="!remoteControlCommand || selected.length == 0"
                @click="remoteControl()"
                color="success"
                class="ma-3"
                :loading="remoteControlPending"
                >Execute</v-btn
              >
              <v-btn
                v-if="this.remoteControlPending"
                text
                small
                @click="cancelAllRemoteControlTasks()"
                >Cancel</v-btn
              >
            </v-col>
          </v-row>
          <v-row
            align="center"
            justify="center"
            v-if="
              remoteControlCommand == 'GetProperty' ||
              remoteControlCommand == 'SetProperty'
            "
          >
            <v-col>
              <v-combobox
                :disabled="
                  remoteControlCommand != 'GetProperty' &&
                  remoteControlCommand != 'SetProperty'
                "
                v-model="remoteControlPropertyName"
                :items="remoteControlPropertyNames"
              ></v-combobox>
            </v-col>
            <v-col>
              <v-text-field
                v-if="remoteControlCommand == 'SetProperty'"
                :disabled="remoteControlCommand != 'SetProperty'"
                v-model="remoteControlPropertyValue"
                label="Property Value"
              ></v-text-field>
            </v-col>
          </v-row>
        </v-container>

        <v-container
          v-if="remoteControlEngaged"
          transition="scroll-y-transition"
        >
          <v-row
            align="stretch"
            justify="center"
            v-for="task in remoteControlTasksInFlight"
            :key="task.taskId"
          >
            <v-col>
              <v-slide-y-transition>
                <v-card
                  class="pa-2"
                  outlined
                  tile
                  :loading="
                    task.pending &&
                    (task.results.length == 0 || task.messages.length == 0)
                  "
                >
                  <v-card-title>{{ task.usr }}</v-card-title>
                  <v-card-subtitle>{{ task.taskName }}</v-card-subtitle>
                  <v-card-text>
                    <p
                      v-if="
                        task.pending &&
                        (task.results.length == 0 || task.messages.length == 0)
                      "
                    >
                      Waiting on response...
                    </p>
                    <span
                      v-for="taskResult in task.results"
                      :key="taskResult.id"
                    >
                      <json-viewer
                        :expand-depth="5"
                        copyable
                        boxed
                        sort
                        :value="taskResult.result"
                        v-if="Object.entries(taskResult.result).length"
                      ></json-viewer>
                    </span>
                    <span
                      v-for="taskMessage in task.messages"
                      :key="taskMessage.index"
                    >
                      <p v-if="taskMessage.message">
                        Message: {{ taskMessage.message }}
                      </p>
                    </span>
                    <p
                      v-if="
                        task.pending &&
                        (task.results.length > 0 || task.messages.length > 0)
                      "
                    >
                      Got a result, waiting for more...
                      <v-progress-circular
                        indeterminate
                        color="primary"
                        :width="3"
                        size="20"
                      ></v-progress-circular>
                    </p>
                  </v-card-text>
                  <v-card-actions></v-card-actions>
                </v-card>
              </v-slide-y-transition>
            </v-col>
          </v-row>
        </v-container>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { API, graphqlOperation } from "aws-amplify";
import * as queries from "@/graphql/queries";
import * as mutations from "@/graphql/mutations";
import * as subscriptions from "@/graphql/subscriptions";
import wsproperties from "@/assets/wsproperties.json";
import pmstypes from "@/assets/pmsTypes.json";
import pmsConnectionStatusTypes from "@/assets/pmsConnectionStatusTypes.json";

export default {
  data: function () {
    return {
      items: [],
      loading: false,
      nextToken: "",
      remoteControlEngaged: false,
      resultsFilter: "",
      selected: [],
      searchFormValid: false,
      searchType: "heartbeat",
      lastSearchType: "heartbeat",
      conditionType: "ge",
      pmsType: "bp",
      pmsConnectionStatusType: "OK",
      filterTextVal: "",
      filterTextValTo: "",
      filterDateVal: new Date(),
      filterDateValTo: new Date(),
      filterPmsTypeVal: "",
      filterStatusTypeVal: "",
      dateConditionTypes: [
        { text: "After", value: "ge" },
        { text: "Before", value: "le" },
        { text: "Between", value: "between" },
      ],
      conditionTypes: [
        { text: "After", value: "ge" },
        { text: "Begins With", value: "beginsWith" },
        { text: "Is", value: "eq" },
        { text: "Between", value: "between" },
        { text: "Before", value: "le" },
      ],
      fixedValConditionTypes: [{ text: "Is", value: "eq" }],
      searchTypes: [
        {
          value: "availability",
          type: "date",
          text: "Availability Processed",
          label: "Availability Processed",
          labelTo: "Availability Processed Limit",
        },
        {
          value: "heartbeat",
          type: "date",
          text: "Heartbeat Received",
          label: "Hearbeat Received",
          labelTo: "Heartbeat Received Limit",
        },
        {
          value: "sub",
          type: "textNoFilter",
          text: "PMS Connector ID",
          label: "Required. Sub / Notification Channel of PMS Connector",
          hint:
            "This is a GUID value. You will see it in the URL after navigating into a PMS Connector screen.",
        },
        {
          value: "username",
          type: "text",
          text: "PMS Connector Name",
          label: "Username",
          hint: "Required. Case sensitive.",
          labelTo: "Username limit",
          hintTo: "Required. Case sensitive.",
        },

        {
          value: "version",
          type: "text",
          text: "PMS Connector Version",
          label: "PMS Connector Version",
          hint: "Required",
          labelTo: "Version Limit",
          hintTo: "Required",
        },
        { value: "status", type: "statusType", text: "PMS Status" },

        { value: "pms", type: "pmsType", text: "PMS Type" },
        /* { value: "accounts", type: "text", text: "Product Count" },*/
        {
          value: "provisioning",
          type: "date",
          text: "Provisioning Date",
          label: "Provisioned On",
          labelTo: "Provisioned Until",
        },
      ],
      searchTextValue: "",
      /*   provisioning: TableStringFilterInput #GSI1 # Date Range
  heartbeat: TableStringFilterInput #GSI3 # Date Range
  availability: TableStringFilterInput  # GSI5 # Date Range
  status: TableStringFilterInput  # GSI4 # Enum of status values
  username: TableStringFilterInput  # GSI6 # Free text input
  sub: String # free text input
  accounts: TableStringFilterInput #GSI2 # number / range
  pms: TableStringFilterInput #GSI7 # enum of pms types
  version: TableStringFilterInput #GSI8 # Free text input
  */
      listOptions: {
        sortBy: ["ctdTs"],
        sortDesc: [true],
      },
      headers: [
        {
          text: "Name",
          align: "start",
          sortable: true,
          value: "usr",
          groupable: false,
        },
        { text: "Version", value: "appVersion", sortable: true },
        { text: "Brand", value: "brand", sortable: true },

        { text: "PMS Type", value: "pmsType", sortable: true },

        {
          text: "Heartbeat",
          value: "heartbeatRcvd",
          sortable: true,
          groupable: false,
        },
        {
          text: "Availability",
          value: "availabilityLastProcTs",
          sortable: true,
          groupable: false,
        },
        {
          text: "PMS Status",
          value: "pmsStatus",
          sortable: true,
          groupable: false,
        },
        {
          text: "Provisioned",
          value: "ctdTs",
          sortable: true,
          groupable: false,
        },
      ],
      remoteControlPending: false,
      remoteControlResults: {},
      remoteControlTasksInFlight: [],
      remoteControlTimeout: null,
      remoteControlMessage: "",
      remoteControlErrorMessage: "",
      remoteControlPropertyName: "",
      remoteControlPropertyValue: "",
      remoteControlPropertyNames: [],
      remoteControlCommand: "list",
      remoteControlCommands: [
        { text: "Get Process Details", value: "list" },
        { text: "Get Appointment Book Details" },
        {
          text: "Get Auto Sync Status",
          value: "Get automatic synchronisation status",
        },
        { text: "Read Config Property", value: "GetProperty" },
        { text: "Write Config Property", value: "SetProperty" },
        { text: "Restart Sync", value: "Restart" },
        { text: "Remote Update Sync", value: "Auto Update" },
      ],

      textFieldProps: {
        appendIcon: "mdi-calendar",
        dense: true,
        class: "",
      },
      pmsTypes: [],
      pmsConnectionStatusTypes: [],
    };
  },
  created: async function () {
    const start = new Date();
    start.setHours(0, 0, 0);
    start.setDate(start.getDate() - 3); // 3 days ago by default

    const toDate = new Date();
    toDate.setHours(0, 0, 0);
    toDate.setDate(toDate.getDate() + 1);

    this.filterDateVal = start;
    this.filterDateValTo = toDate;
    this.remoteControlPropertyNames = wsproperties.items;
    this.pmsTypes = pmstypes.items;
    this.pmsConnectionStatusTypes = pmsConnectionStatusTypes.items;
    await this.getData();
  },
  computed: {
    headersForSearchType: function () {
      console.log("headersForSearchType", this.lastSearchType);

      let headers = [];

      switch (this.lastSearchType) {
        case "sub": {
          headers = this.headers;
          break;
        }
        case "availability": 
        {
          headers = this.headers.filter(
            (h) =>
              [
                "appVersion",
                "heartbeatRcvd",
                "pmsStatus",
              ].indexOf(h.value) < 0
          );
          break;
      }
          case "provisioning":
          case "pms":
          case "username": {

          headers = this.headers.filter(
            (h) =>
              [
                "appVersion",
                "heartbeatRcvd",
                "availabilityLastProcTs",
                "pmsStatus",
              ].indexOf(h.value) < 0
          );
          break;
        }
        default: {
          headers = this.headers.filter(
            (h) =>
              [
                "availabilityLastProcTs",
              ].indexOf(h.value) < 0
          );
          break;
        }
      }

      console.log("headersForSearchType", headers);
      return headers;
    },
    selectedSearchType: function () {
      return this.searchTypes.find((t) => t.value === this.searchType);
    },
    conditionTypesForSearchType: function () {
      switch (this.selectedSearchType.type) {
        case "date": {
          return this.dateConditionTypes;
          break;
        }
        case "pmsType": {
          return this.fixedValConditionTypes;
          break;
        }
        case "statusType": {
          return this.fixedValConditionTypes;
          break;
        }
        case "textNoFilter": {
          return this.fixedValConditionTypes;
          break;
        }
        default: {
          return this.conditionTypes;
          break;
        }
      }
    },
  },
  filters: {
    pmsStatus: function (value) {
      switch (value) {
        case "OK":
          return "Connected";
          break;
        case "NotImplemented":
          return "n/a";
          break;
        case "NotContactable_Other":
          return "Disconnected";
          break;
        default:
          return value;
      }
    },
  },
  watch: {
    searchType: function () {
      if (
        !this.conditionTypesForSearchType.find(
          (t) => t.value === this.conditionType
        )
      ) {
        this.conditionType = this.conditionTypesForSearchType[0].value;
      }
    },
  },
  methods: {
    getSearchFilter: function () {
      const searchType = this.selectedSearchType?.type ?? "text";
      const conditionType = this.conditionType;
      const filterName = this.selectedSearchType?.value ?? "username";

      if (searchType == "textNoFilter") {
        return { [filterName]: this.filterTextVal };
      }

      let filterVal = "";
      let filterValTo = "";

      switch (searchType) {
        case "date": {
          filterVal = this.filterDateVal?.getTime()?.toString() ?? "";
          filterValTo = this.filterDateValTo?.getTime()?.toString() ?? "";
          break;
        }
        case "pmsType": {
          filterVal = this.pmsType;
          break;
        }
        case "statusType": {
          filterVal = this.pmsConnectionStatusType;
          break;
        }
        default: {
          // default to text
          filterVal = this.filterTextVal;
          filterValTo = this.filterTextValTo;
        }
      }

      const value =
        conditionType === "between" ? [filterVal, filterValTo] : filterVal;

      return {
        [filterName]: {
          [conditionType]: value,
        },
      };
    },
    getData: async function (thisToken) {
      this.loading = true;

      try {
        console.log(`making call with token ${thisToken ? true : false}`);

        const filter = this.getSearchFilter();

        console.info("getSearchFilter returned", filter);

        const result = await API.graphql(
          graphqlOperation(queries.searchStatus, {
            filter,
            nextToken: thisToken,
            limit: 200,
          })
        );

        if (!result?.data?.searchStatus) {
          this.handleResponseError("Get Data", result);

          return;
        }

        let items = result?.data?.searchStatus?.items ?? [];
        const nextToken = result?.data?.searchStatus?.nextToken ?? "";

        if (nextToken) {
          this.nextToken = nextToken;
        } else {
          this.nextToken = "";
        }

        if (thisToken) {
          this.items = this.items.concat(items);
        } else {
          this.items = items;
          this.lastSearchType = this.searchType;
        }

        

        //this.results = result.data.listStatusByProvisionTs;
      } catch (err) {
        this.handleResponseError("Get Data", err);
      } finally {
        if (!thisToken) {
          this.loading = false;
        }
      }
    },
    async handleResponseError(operationDescription, response) {
      console.log("handleResponseError", response);
      const notifyResult = await this.$dialog.notify.error(
        operationDescription,
        {
          position: "top-right",
          timeout: 5000,
          actions: [{ text: "Info", "x-small": true, color: "secondary" }],
        }
      );

      if (notifyResult == "Info") {
        this.$dialog.error({
          text: JSON.stringify(response?.errors || response),
          title: operationDescription,
          icon: "mdi-error",
        });
      }
    },
    cancelAllRemoteControlTasks() {
      for (const task of this.remoteControlTasksInFlight) {
        if (task.subscription) {
          task.subscription.unsubscribe();
          task.pending = false;
        }
      }

      //this.remoteControlTasksInFlight = [];

      this.remoteControlMessage = ``;
      this.remoteControlResults = {};
      this.remoteControlErrorMessage = "";
      this.remoteControlPending = false;
    },
    cancelRemoteControlTaskWait(taskId) {
      const thisTask = this.remoteControlTasksInFlight.find(
        (i) => i.taskId == taskId
      );

      if (!thisTask) {
        return;
      }

      if (thisTask.subscription) {
        thisTask.subscription.unsubscribe();
        thisTask.pending = false;
      }
    },
    async remoteControl() {
      if (this.remoteControlTimeout) {
        clearTimeout(this.remoteControlTimeout);
      }
      this.cancelAllRemoteControlTasks();
      this.remoteControlTasksInFlight = [];
      const taskTypeInput = this.remoteControlCommand;
      console.log("remoteControl", taskTypeInput);

      for (const thisConnector of this.selected) {
        const thisSub = thisConnector.sub;
        const command = "TaskRequest";
        const taskType = `RemoteControl_${taskTypeInput}`;
        const taskName =
          this.remoteControlCommands.find((c) => c.value == taskTypeInput)
            ?.text ?? taskTypeInput;
        const parameters =
          taskTypeInput.indexOf("Property") >= 0
            ? JSON.stringify({
                propertyName: this.remoteControlPropertyName,
                propertyValue: this.remoteControlPropertyValue,
              })
            : "{}";

        console.log(
          "sending taskRequest",
          thisSub,
          command,
          taskType,
          parameters,
          thisConnector
        );

        const taskRequest = await API.graphql(
          graphqlOperation(mutations.publishTaskRequest, {
            sub: thisSub,
            command,
            taskType,
            parameters,
          })
        );

        console.log("task request response", taskRequest);

        const taskId = taskRequest?.data?.publishTaskRequest?.taskId ?? "";

        if (taskId) {
          this.remoteControlMessage = `Waiting on task ID ${taskId}`;
          this.remoteControlResults = {};
          this.remoteControlErrorMessage = "";
          this.remoteControlPending = true;

          const subscription = API.graphql(
            graphqlOperation(subscriptions.onPublishTaskResult, {
              taskId: taskId,
            })
          ).subscribe({
            next: (result) => {
              console.log("onPublishTaskResult", result);

              /*
              this.remoteControlTasksInFlight = this.remoteControlTasksInFlight.filter(
                i => i.taskId !== taskId
              );
              subscription.unsubscribe();
              */

              const msg =
                result?.value?.data?.onPublishTaskResult?.message ?? "";
              const err =
                result?.value?.data?.onPublishTaskResult?.errorMessage ?? "";
              const resultObj =
                result?.value?.data?.onPublishTaskResult?.result ?? {};

              const thisTask = this.remoteControlTasksInFlight.find(
                (i) => i.taskId == taskId
              );
              //thisTask.pending = false;
              const responseId = `${taskId}_${new Date().getTime()}`;

              if (resultObj.length > 0) {
                const parsedResult = JSON.parse(resultObj);

                this.remoteControlResults = parsedResult;

                thisTask.results.push({ result: parsedResult, id: responseId });
              }
              //this.remoteControlResults = resultObj;
              this.remoteControlMessage = msg;
              this.remoteControlErrorMessage = err;

              thisTask.messages.push({ message: msg, id: responseId });

              if (
                this.remoteControlTasksInFlight.filter((i) => i.pending == true)
                  .length == 0
              ) {
                this.remoteControlPending = false;
              }
            },
          });

          this.remoteControlTasksInFlight.push({
            taskId,
            name: `${thisConnector.usr} - ${taskType} - ${taskId}`,
            usr: thisConnector.usr,
            taskType: taskType,
            results: [],
            messages: [],
            pending: true,
            subscription,
            taskName,
          });
        }
      }
      this.remoteControlTimeout = setTimeout(() => {
        this.cancelAllRemoteControlTasks();
      }, 30000);
    },
  },
};
</script>